import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserProfile } from "@clerk/react";
import { KeyRound, Mail, IdCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errMsg, SUPPORT_TEL } from "@/lib/api";
import { useClerkAvailable } from "@/components/auth/ClerkAuthProvider";
import { useLocalAuth } from "@/components/auth/LocalAuth";
import { usePanel } from "./PanelContext";

export default function Account() {
  const { api, biz, reload, me } = usePanel();
  const clerkAvailable = useClerkAvailable();
  const { user: localUser, logout } = useLocalAuth();
  const [f, setF] = useState({ name: biz.name, phone: biz.phone, address: biz.address });
  const [ef, setEf] = useState({ email: "", password: "" });
  const [pf, setPf] = useState({ current_password: "", new_password: "" });
  useEffect(() => { setF({ name: biz.name, phone: biz.phone, address: biz.address }); }, [biz]);

  const publicId = me?.user?.public_id || localUser?.public_id || "—";

  const save = async () => {
    try { await api.put("/business", f); await reload(); toast.success("İşletme bilgileri kaydedildi"); } catch (e) { toast.error(errMsg(e)); }
  };
  const changeEmail = async () => {
    try {
      const { data } = await api.post("/auth/change-email", ef);
      localStorage.setItem("gs_auth_token", data.access_token);
      setEf({ email: "", password: "" });
      await reload();
      toast.success("E-posta adresiniz güncellendi");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const changePassword = async () => {
    try {
      await api.post("/auth/change-password", pf);
      setPf({ current_password: "", new_password: "" });
      toast.success("Şifreniz güncellendi");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6 rise" data-testid="account-page">
      <div><h1 className="text-3xl font-extrabold">Hesap Ayarları</h1><p className="text-sm text-slate-500">İşletme bilgilerinizi, e-posta adresinizi ve şifrenizi buradan değiştirin.</p></div>

      <div className="rounded-3xl bg-[#0F1F45] text-white p-6 flex flex-wrap items-center gap-5" data-testid="account-id-card">
        <IdCard className="h-9 w-9 text-[#FF6B0A]" />
        <div>
          <p className="text-xs uppercase tracking-widest text-white/60 font-bold">Kullanıcı Numaranız</p>
          <p className="font-mono-num text-3xl font-black tracking-[0.3em]" data-testid="account-public-id">{publicId}</p>
        </div>
        <p className="text-xs text-white/50 ml-auto max-w-xs">Bu 6 haneli numara size özeldir; destek taleplerinde paylaşın. Numaranızı sadece siz ve süper yönetici görebilir.</p>
      </div>

      <div className="rounded-3xl bg-white border p-6 grid sm:grid-cols-3 gap-4 items-end">
        <div><Label>İşletme adı</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="account-business-name-input" /></div>
        <div><Label>Telefon</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="account-business-phone-input" /></div>
        <div><Label>Adres</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="account-business-address-input" /></div>
        <Button className="rounded-full bg-[#FF6B0A] hover:bg-[#E55A00] sm:col-span-3 sm:w-fit" onClick={save} data-testid="account-business-save-btn">Kaydet</Button>
      </div>

      {localUser ? (
        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-3xl bg-white border p-6 space-y-4" data-testid="account-email-card">
            <p className="font-display font-bold flex items-center gap-2"><Mail className="h-5 w-5 text-[#FF6B0A]" /> E-posta Değiştir</p>
            <p className="text-xs text-slate-500">Mevcut: <b>{me?.user?.email || localUser.email}</b></p>
            <div><Label>Yeni e-posta</Label><Input type="email" value={ef.email} onChange={(e) => setEf({ ...ef, email: e.target.value })} data-testid="account-new-email-input" /></div>
            <div><Label>Şifreniz (onay)</Label><Input type="password" value={ef.password} onChange={(e) => setEf({ ...ef, password: e.target.value })} data-testid="account-email-password-input" /></div>
            <Button className="rounded-full bg-[#0F1F45] w-full" onClick={changeEmail} disabled={!ef.email || !ef.password} data-testid="account-email-save-btn">E-postayı Güncelle</Button>
          </div>
          <div className="rounded-3xl bg-white border p-6 space-y-4" data-testid="account-password-card">
            <p className="font-display font-bold flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#FF6B0A]" /> Şifre Değiştir</p>
            <div><Label>Mevcut şifre</Label><Input type="password" value={pf.current_password} onChange={(e) => setPf({ ...pf, current_password: e.target.value })} data-testid="account-current-password-input" /></div>
            <div><Label>Yeni şifre (en az 6 karakter)</Label><Input type="password" value={pf.new_password} onChange={(e) => setPf({ ...pf, new_password: e.target.value })} data-testid="account-new-password-input" /></div>
            <Button className="rounded-full bg-[#0F1F45] w-full" onClick={changePassword} disabled={!pf.current_password || pf.new_password.length < 6} data-testid="account-password-save-btn">Şifreyi Güncelle</Button>
            <p className="text-xs text-slate-500">Şifrenizi unutursanız bize <a href={SUPPORT_TEL} className="font-semibold text-[#FF6B0A]">0542 396 12 60</a> numarası üzerinden ulaşabilirsiniz.</p>
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button variant="outline" className="rounded-full" onClick={logout} data-testid="account-logout-btn">Çıkış Yap</Button>
          </div>
        </div>
      ) : clerkAvailable ? (
        <div>
          <h2 className="font-display text-xl font-bold mb-3">E-posta ve Şifre</h2>
          <p className="text-sm text-slate-500 mb-4">“Güvenlik” sekmesinden şifrenizi, “Profil” sekmesinden e-posta adresinizi değiştirebilirsiniz.</p>
          <div className="overflow-x-auto" data-testid="account-user-profile"><UserProfile routing="hash" /></div>
        </div>
      ) : null}
    </div>
  );
}
